import type { AppVoiceTrackId } from "@/audio/app-catalog";
import type {
  AudioPlaybackResult,
  AudioUnlockResult
} from "@/audio/runtime";

export interface NarrationCoordinatorDependencies {
  readonly playVoice: (
    trackId: AppVoiceTrackId
  ) => Promise<AudioPlaybackResult>;
  readonly stopVoice: () => void;
  readonly unlock: () => Promise<AudioUnlockResult>;
}

export interface NarrationCoordinator {
  cancel(): void;
  notifyUnlocked(): Promise<AudioPlaybackResult | undefined>;
  request(
    trackId: AppVoiceTrackId
  ): Promise<AudioPlaybackResult | undefined>;
}

interface PendingNarration {
  readonly requestId: number;
  readonly trackId: AppVoiceTrackId;
}

export function createNarrationCoordinator({
  playVoice,
  stopVoice,
  unlock
}: NarrationCoordinatorDependencies): NarrationCoordinator {
  let nextRequestId = 0;
  let pending: PendingNarration | null = null;

  const flush = async (
    request: PendingNarration,
    shouldUnlock: boolean
  ): Promise<AudioPlaybackResult | undefined> => {
    if (shouldUnlock) {
      const unlockResult = await unlock();
      if (unlockResult.status !== "unlocked") {
        return undefined;
      }
    }

    if (pending !== request) {
      return undefined;
    }

    pending = null;
    return playVoice(request.trackId);
  };

  return {
    cancel() {
      nextRequestId += 1;
      pending = null;
      stopVoice();
    },
    notifyUnlocked() {
      return pending === null
        ? Promise.resolve(undefined)
        : flush(pending, false);
    },
    request(trackId) {
      const request: PendingNarration = {
        requestId: nextRequestId + 1,
        trackId
      };
      nextRequestId = request.requestId;
      pending = request;
      return flush(request, true);
    }
  };
}
